const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.beternakayam.com/api/v1'

let accessToken: string | null = localStorage.getItem('access_token')
let refreshToken: string | null = localStorage.getItem('refresh_token')
let isRefreshing = false
let refreshQueue: Array<{
  resolve: (token: string) => void
  reject: (err: Error) => void
}> = []

export function setTokens(access: string, refresh: string) {
  accessToken = access
  refreshToken = refresh
  localStorage.setItem('access_token', access)
  localStorage.setItem('refresh_token', refresh)
}

export function clearTokens() {
  accessToken = null
  refreshToken = null
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

export function getAccessToken(): string | null {
  return accessToken
}

// Sinkronisasi antar-tab: login/logout di satu tab langsung berlaku di tab lain.
// Event 'storage' hanya fire di tab LAIN, bukan tab yang menulis.
window.addEventListener('storage', (e) => {
  if (e.key === 'access_token') accessToken = e.newValue
  if (e.key === 'refresh_token') refreshToken = e.newValue
})

// Backend membungkus semua respons sukses dalam {data, message}
function unwrap<T>(json: unknown): T {
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data
  }
  return json as T
}

async function refreshAccessToken(): Promise<string> {
  if (!refreshToken) throw new Error('No refresh token')

  const res = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!res.ok) {
    clearTokens()
    throw new Error('Refresh failed')
  }

  const data = unwrap<{ access_token: string; refresh_token: string }>(await res.json())
  setTokens(data.access_token, data.refresh_token)
  return data.access_token
}

async function handleRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true
  try {
    const token = await refreshAccessToken()
    refreshQueue.forEach(({ resolve }) => resolve(token))
    return token
  } catch (err) {
    refreshQueue.forEach(({ reject }) => reject(err as Error))
    throw err
  } finally {
    refreshQueue = []
    isRefreshing = false
  }
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  let res = await fetch(`${BASE_URL}${url}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  // Auto-refresh on 401
  if (res.status === 401 && refreshToken && !url.includes('/auth/refresh')) {
    try {
      const newToken = await handleRefresh()
      headers['Authorization'] = `Bearer ${newToken}`
      res = await fetch(`${BASE_URL}${url}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      })
    } catch {
      clearTokens()
      const currentPath = window.location.pathname
      if (currentPath !== '/login') {
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
      }
      throw new Error('Session expired')
    }
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new ApiError(res.status, error.message || error.error || 'Request failed', error)
  }

  if (res.status === 204) return undefined as T
  return unwrap<T>(await res.json())
}

export class ApiError extends Error {
  status: number
  data: unknown

  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

export const api = {
  get<T>(url: string): Promise<T> {
    return request<T>('GET', url)
  },
  post<T>(url: string, body?: unknown): Promise<T> {
    return request<T>('POST', url, body)
  },
  patch<T>(url: string, body?: unknown): Promise<T> {
    return request<T>('PATCH', url, body)
  },
  delete<T>(url: string): Promise<T> {
    return request<T>('DELETE', url)
  },
}
